// =============================================================================
// src/features/explore/explore-vote-anim.ts — Animacao + formatacao dos rotulos
//   de voto (entrada escalonada das bolhas + count-up dos numeros).
//
// Proveniencia de padrao: src/features/copilot-chat/copilot-charts.ts (rAF +
//   reduce-motion). Aqui o count-up usa um loop por FRAMES (numero fixo de passos)
//   — e NAO por timestamp — porque o stub de requestAnimationFrame nos testes
//   reexecuta o callback sincronamente com t fixo; um loop por tempo recursaria sem
//   fim. Com passos fixos a recursao termina (profundidade = nº de passos).
//
// SRP: so animacao/formatacao. Sem Leaflet, sem dataset. Reusa prefersReducedMotion.
// =============================================================================

import { prefersReducedMotion } from './explore-helpers';
import { STAGGER_MS, STAGGER_CAP_MS, COUNTUP_MS } from './explore-vote-constants';

/** Duracao nominal de um frame (ms) — divide o count-up em passos fixos. */
const FRAME_MS = 16;

/**
 * True quando a aba esta oculta: o rAF e throttled (~1fps) e o count-up ficaria
 * preso num valor baixo por segundos. O numero FINAL ja esta no DOM (baked), entao
 * fora de foco mantemos o final em vez de regredir (principio: reveal so realca um
 * default ja visivel).
 */
function documentHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden;
}

/** Ease-out cubico: rapido no inicio, suave no fim (sem bounce). */
export function easeOutCubic(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return 1 - Math.pow(1 - c, 3);
}

/** Inteiro com agrupamento pt-BR (ex.: 283315 -> "283.315"). */
export function formatVotos(value: number): string {
  return Math.round(value).toLocaleString('pt-BR');
}

/** Percentual pt-BR com casas fixas (ex.: 54.3 -> "54,3%"; 6.34,2 -> "6,34%"). */
export function formatPercent(value: number, decimals = 1): string {
  const text = value.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return `${text}%`;
}

/** Colocacao ordinal (ex.: 1 -> "1º"). */
export function formatRank(n: number): string {
  return `${n}º`;
}

/**
 * Anima o texto de um elemento de 0 ate `target`, formatando cada frame com `format`.
 *   Sob reduce-motion (ou duracao <= 0) escreve o valor final de imediato. Termina
 *   em nº fixo de passos (seguro sob o stub sincrono de rAF).
 */
export function countUpLabel(el: HTMLElement | null, target: number, format: (v: number) => string, durationMs = COUNTUP_MS): void {
  if (!el) return;
  if (prefersReducedMotion() || durationMs <= 0 || documentHidden()) { el.textContent = format(target); return; }
  const steps = Math.max(1, Math.round(durationMs / FRAME_MS));
  runCountUp(el, target, format, steps);
}

/** Loop interno do count-up: avanca um frame por chamada de rAF ate fechar os passos. */
function runCountUp(el: HTMLElement, target: number, format: (v: number) => string, steps: number): void {
  let frame = 0;
  const tick = (): void => {
    frame += 1;
    if (frame >= steps) { el.textContent = format(target); return; }
    el.textContent = format(target * easeOutCubic(frame / steps));
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/**
 * Revela `count` itens em cascata: chama `onReveal(i)` com atraso crescente
 *   (i*STAGGER_MS, teto STAGGER_CAP_MS). Sob reduce-motion revela todos de imediato.
 */
export function staggerReveal(count: number, onReveal: (index: number) => void): void {
  if (prefersReducedMotion()) {
    for (let i = 0; i < count; i += 1) onReveal(i);
    return;
  }
  for (let i = 0; i < count; i += 1) {
    setTimeout(() => onReveal(i), Math.min(i * STAGGER_MS, STAGGER_CAP_MS));
  }
}
